import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Dialog, Field, Icon, IconButton, Input, Select } from "../../ds/DS";
import type { Tone } from "../../ds/DS";
import { api } from "../../lib/api";
import { relativeTime } from "../tone";
import type { PageProps } from "../Portal";
import { DialogFoot, PageHead } from "./parts";
import { plural } from "./util";

interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "client";
  title_role: string;
  projects_access: string;
  vault_access: string;
  last_seen_at: number | null;
  created_at?: number;
}

interface ApiStaff {
  id: string;
  email: string;
  name: string;
  title_role: string;
  last_seen_at: number | null;
}

const TITLE_ROLES = ["Owner", "Member", "Billing"];
const VAULT_ACCESS = ["Full", "Production only", "None"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function initials(name: string, email: string) {
  const base = name.trim() || email;
  return base
    .split(/[\s@.]+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Never signed in = still an invite.
const invited = (u: ApiUser) => u.last_seen_at == null;

function PersonDialog({
  clientId,
  user,
  projectNames,
  onClose,
  onSaved,
  onRemove,
}: {
  clientId: string;
  user: ApiUser | null;
  projectNames: string[];
  onClose: () => void;
  onSaved: (email: string) => void;
  onRemove: (u: ApiUser) => void;
}) {
  const [f, setF] = useState(() => ({
    email: user?.email ?? "",
    name: user?.name ?? "",
    titleRole: user?.title_role ?? "Member",
    projectsAccess: user?.projects_access ?? "All projects",
    vaultAccess: user?.vault_access ?? "None",
    password: "",
  }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const projectsOptions = ["All projects", ...projectNames];
  const vaultOptions = VAULT_ACCESS.includes(f.vaultAccess)
    ? VAULT_ACCESS
    : [...VAULT_ACCESS, f.vaultAccess];
  const emailBad = !user && !EMAIL_RE.test(f.email.trim());
  // Optional on invite: set one now, else the welcome email carries a link.
  const passwordBad = !user && f.password.length > 0 && f.password.length < 10;

  const save = async () => {
    setTried(true);
    if (emailBad || passwordBad) return;
    setBusy(true);
    setErr("");
    try {
      if (user) {
        await api.patch(`/clients/${clientId}/users/${user.id}`, {
          name: f.name,
          titleRole: f.titleRole,
          projectsAccess: f.projectsAccess,
          vaultAccess: f.vaultAccess,
        });
      } else {
        await api.post(`/clients/${clientId}/users`, {
          email: f.email.trim(),
          name: f.name.trim(),
          titleRole: f.titleRole,
          projectsAccess: f.projectsAccess,
          vaultAccess: f.vaultAccess,
          password: f.password || undefined,
        });
      }
      onSaved(f.email.trim());
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={user ? "Edit access" : "Invite someone"}
      description={
        user
          ? user.email
          : "They'll get a welcome email. Set a password now, or leave it blank and they'll choose their own from a link."
      }
      accent
      width={560}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel={user ? "Save" : "Send invite"}
          busy={busy}
          invalid={tried && (emailBad || passwordBad)}
          onCancel={onClose}
          onSave={() => void save()}
          deleteLabel={user ? "Remove access" : undefined}
          deleteWhat={user ? `${user.name || user.email}'s access` : undefined}
          onDelete={user ? () => onRemove(user) : undefined}
        />
      }
    >
      <div className="stack-16">
        {err ? <div className="form-err">{err}</div> : null}
        {user ? null : (
          <Field label="Email" required>
            <Input
              type="email"
              value={f.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="name@company.co.uk"
              aria-invalid={tried && emailBad}
            />
            {tried && emailBad ? (
              <span className="field-err">Enter a valid email address, like name@company.co.uk.</span>
            ) : null}
          </Field>
        )}
        <Field label="Name">
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Role">
            <Select
              options={TITLE_ROLES}
              value={f.titleRole}
              onChange={(e) => set("titleRole", e.target.value)}
            />
          </Field>
          <Field label="Projects">
            <Select
              options={projectsOptions}
              value={
                projectsOptions.includes(f.projectsAccess) ? f.projectsAccess : "All projects"
              }
              onChange={(e) => set("projectsAccess", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Vault access" hint="Billing users never see the vault.">
          <Select
            options={vaultOptions}
            value={f.vaultAccess}
            onChange={(e) => set("vaultAccess", e.target.value)}
          />
        </Field>
        {user ? null : (
          <Field
            label="Password"
            hint="Optional — at least 10 characters. Leave blank to email them a set-password link."
          >
            <Input
              type="password"
              value={f.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="Leave blank for a set-password link"
              autoComplete="new-password"
              aria-invalid={tried && passwordBad}
            />
            {tried && passwordBad ? (
              <span className="field-err">
                Password must be at least 10 characters (or leave it blank).
              </span>
            ) : null}
          </Field>
        )}
      </div>
    </Dialog>
  );
}

interface Row {
  key: string;
  name: string;
  email: string;
  ini: string;
  team: boolean; // Agile Software
  role: string;
  roleTone: Tone;
  invited: boolean;
  projects: string;
  vault: string;
  seen: string;
  user?: ApiUser;
}

export function Team({ isAdmin, client, projects, go, toast }: PageProps) {
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [staff, setStaff] = useState<ApiStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<ApiUser | null | "new">(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ApiUser | null>(null);
  const clientId = client?.id;

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    Promise.all([
      api
        .get<{ users: ApiUser[] }>(`/clients/${clientId}/users`)
        .then((r) => r.users)
        .catch(() => [] as ApiUser[]),
      api
        .get<{ staff: ApiStaff[] }>("/team/staff")
        .then((r) => r.staff)
        .catch(() => [] as ApiStaff[]),
    ])
      .then(([u, s]) => {
        setUsers(u.filter((x) => x.role === "client"));
        setStaff(s);
      })
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(load, [load]);

  const remove = async (u: ApiUser) => {
    if (!clientId) return;
    await api.del(`/clients/${clientId}/users/${u.id}`);
    setDialog(null);
    setRemoving(null);
    load();
    toast(`${u.name || u.email} can no longer sign in.`);
  };
  const resend = async (u: ApiUser) => {
    if (!clientId) return;
    setBusyId(u.id);
    try {
      await api.post(`/clients/${clientId}/users/${u.id}/resend`);
      toast(`Invite resent to ${u.email}.`);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't resend the invite. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const clientRows: Row[] = users.map((u) => ({
    key: u.id,
    name: u.name || (u.email.split("@")[0] ?? u.email),
    email: u.email,
    ini: initials(u.name, u.email),
    team: false,
    role: invited(u) ? "Invited" : u.title_role,
    roleTone: invited(u) ? "warning" : u.title_role === "Owner" ? "teal" : "neutral",
    invited: invited(u),
    projects: u.projects_access,
    vault: u.vault_access,
    seen: invited(u)
      ? u.created_at
        ? `Invited ${new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
        : "Invited"
      : relativeTime(u.last_seen_at as number),
    user: u,
  }));
  const staffRows: Row[] = staff.map((s) => ({
    key: s.id,
    name: s.name || (s.email.split("@")[0] ?? s.email),
    email: s.email,
    ini: initials(s.name, s.email),
    team: true,
    role: "Agile Software",
    roleTone: "navy",
    invited: false,
    projects: "All projects",
    vault: "Full",
    seen: s.last_seen_at ? relativeTime(s.last_seen_at) : "—",
  }));
  const groups = [
    { key: "client", heading: client?.name ?? "Client", rows: clientRows },
    { key: "team", heading: "Agile Software", rows: staffRows },
  ];

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="TEAM & ACCESS"
        title="Team & access"
        sub="Who on your side can sign in, and what they can reach."
        actions={
          isAdmin ? (
            <Button icon="user-plus" onClick={() => setDialog("new")}>
              Invite someone
            </Button>
          ) : (
            <Button variant="secondary" icon="message-square" onClick={() => go("tickets")}>
              Ask for a change
            </Button>
          )
        }
      />
      {!isAdmin ? (
        <div className="page-note">
          <Icon name="shield-check" size={15} />
          We manage access for you. Ask for a change and we'll make it the same working day.
        </div>
      ) : null}

      {loading ? (
        <div className="list-card">
          <div className="list-empty">Loading…</div>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="list-card">
            <div className="list-card-head">
              <span className="list-card-title">{g.heading}</span>
              <span className="mono list-card-count">{plural(g.rows.length, "person", "people")}</span>
            </div>
            <div className="grid-row grid-head team-cols">
              <span>Person</span>
              <span>Role</span>
              <span>Projects</span>
              <span>Vault access</span>
              <span>Last seen</span>
              <span />
            </div>
            {g.rows.length === 0 ? (
              <div className="list-empty">No people yet.</div>
            ) : (
              g.rows.map((r) => (
                <div key={r.key} className="grid-row team-cols">
                  <div className="person">
                    <span className={"avatar" + (r.team ? " is-team" : "")}>{r.ini}</span>
                    <div className="cell-min">
                      <div className="v-label">{r.name}</div>
                      <div className="cell-sub">{r.email}</div>
                    </div>
                  </div>
                  <span data-label="Role">
                    <Badge tone={r.roleTone} dot={r.invited}>
                      {r.role}
                    </Badge>
                  </span>
                  <span className="cell-body" data-label="Projects">
                    {r.projects}
                  </span>
                  <span className="cell-body" data-label="Vault access">
                    {r.vault}
                  </span>
                  <span className="cell-date mono" data-label="Last seen">
                    {r.seen}
                  </span>
                  <div className="row-6 end">
                    {isAdmin && r.user ? (
                      <>
                        {r.invited ? (
                          <IconButton
                            icon="send"
                            label="Resend invite"
                            variant="ghost"
                            disabled={busyId === r.user.id}
                            onClick={() => void resend(r.user as ApiUser)}
                          />
                        ) : null}
                        <IconButton
                          icon="user-cog"
                          label="Edit access"
                          variant="ghost"
                          onClick={() => setDialog(r.user as ApiUser)}
                        />
                        <IconButton
                          icon="user-x"
                          label="Remove access"
                          variant="ghost"
                          onClick={() => setRemoving(r.user as ApiUser)}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        ))
      )}

      {removing ? (
        <Dialog
          open
          title={`Remove ${removing.name || removing.email}'s access?`}
          description="They can no longer sign in. Their tickets and comments stay."
          accent
          width={480}
          onClose={() => setRemoving(null)}
          footer={
            <DialogFoot
              saveLabel="Remove access"
              busy={false}
              onCancel={() => setRemoving(null)}
              onSave={() => void remove(removing)}
            />
          }
        >
          <span />
        </Dialog>
      ) : null}

      {dialog !== null && clientId ? (
        <PersonDialog
          clientId={clientId}
          user={dialog === "new" ? null : dialog}
          projectNames={projects.map((p) => p.name)}
          onClose={() => setDialog(null)}
          onRemove={(u) => void remove(u)}
          onSaved={(addr) => {
            load();
            toast(dialog === "new" ? `Invite sent to ${addr}.` : "Access updated.");
          }}
        />
      ) : null}
    </div>
  );
}
